import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '@/services/api';
import { toast } from 'react-toastify';

const AuthContext = createContext({
  isAuthenticated: false,
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  checkAuth: async () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await authAPI.verifyToken();
      if (response.success) {
        setIsAuthenticated(true);
        setUser(response.data || { email: response.email });
        return true;
      } else {
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      if (response.success) {
        setIsAuthenticated(true);
        setUser(response.user || { email: credentials.email });
        toast.success('Connexion réussie');
        return { success: true, message: response.message };
      } else {
        toast.error(response.message || 'Erreur de connexion');
        return { success: false, message: response.message || 'Erreur de connexion' };
      }
    } catch (error) {
      toast.error(error.message || 'Erreur de connexion');
      return { success: false, message: error.message || 'Erreur de connexion' };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      toast.success('Déconnexion réussie');
    } catch (error) {
      // console.error('Logout error:', error);
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  useEffect(() => {
    checkAuth().then(() => setLoading(false));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        login,
        logout,
        checkAuth
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
